import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BASE_API_URL, ICurbNTurfCash, IReservation, TransactionEvent, TransactionStatus } from '@curbnturf/entities';
import { generateReservationCntPayment } from '@curbnturf/helpers';
import { Observable, map } from 'rxjs';

const API_URL = BASE_API_URL + 'user';

/** One CurbNTurf Cash transaction, shaped for display. Mirrors the real
 *  `ICurbNTurfCash` row but pre-splits `value`'s sign into `type`/`amount`
 *  so templates don't have to. */
export interface ICashTransactionView {
  type: 'earned' | 'spent';
  amount: number;
  note: string;
  date: string; // ISO
  status: TransactionStatus;
}

function toView(t: ICurbNTurfCash): ICashTransactionView {
  return {
    type: t.value >= 0 ? 'earned' : 'spent',
    amount: Math.abs(t.value),
    note: t.note,
    date: new Date(t.timestamp * 1000).toISOString(),
    status: t.status,
  };
}

/**
 * Real, backend-authoritative guest CurbNTurf Cash ledger — `GET
 * /user/:id/cash-transactions`. Earning (on a real review) and spending (auto-applied
 * at reservation request/instant/accept time) both happen server-side; this service is
 * read-only display. See `booking.service.ts`'s `getAvailableCredit`/`getCreditHistory`
 * for the separate, purely-mock equivalent used by bookings with no real backend
 * counterpart (drafts/previews).
 */
@Injectable({ providedIn: 'root' })
export class CurbNTurfCashService {
  private http = inject(HttpClient);

  private transactions(userId: number): Observable<ICurbNTurfCash[]> {
    return this.http.get<ICurbNTurfCash[]>(`${API_URL}/${userId}/cash-transactions`);
  }

  /** Available balance in dollars — sum of `valid` transactions' (signed) value. */
  getAvailableCredit(userId: number): Observable<number> {
    return this.transactions(userId).pipe(
      map((all) => all.filter((t) => t.status === TransactionStatus.valid).reduce((sum, t) => sum + t.value, 0)),
    );
  }

  /** Full ledger, newest first, for display (e.g. the dashboard's credit breakdown). */
  getCreditHistory(userId: number): Observable<ICashTransactionView[]> {
    return this.transactions(userId).pipe(map((all) => all.map(toView).sort((a, b) => b.date.localeCompare(a.date))));
  }

  /** CurbNTurf Cash the server put toward a reservation. Real bookings don't carry it in
   *  `priceComponents` — it's a separate ledger row, found by the deterministic note
   *  `generateReservationCntPayment()` gives it (as the legacy curbnturf app's
   *  `reservation-card.component.ts` does). 0 when none was applied. */
  getCreditAppliedToReservation(userId: number, reservationId: number): Observable<number> {
    const note = generateReservationCntPayment({ id: reservationId } as IReservation);
    return this.getCreditHistory(userId).pipe(
      map((history) => history.find((t) => t.note === note && t.type === 'spent')?.amount ?? 0),
    );
  }

  /** Total earned specifically from referring friends (valid `referral`-event transactions) —
   *  the real figure behind the /refer page's "friends joined" share card. */
  getReferralCredit(userId: number): Observable<number> {
    return this.transactions(userId).pipe(
      map((all) =>
        all
          .filter((t) => t.status === TransactionStatus.valid && t.event === TransactionEvent.referral)
          .reduce((sum, t) => sum + t.value, 0),
      ),
    );
  }
}
